import React, { useEffect, useState } from 'react';
import { getEvents, formatEventDescription } from '../../modules/forecast-v3/api/events';
import { selectAppliedEvents } from './selectAppliedEvents';
import './ForecastChangeHistory.css';

/**
 * What an applied forecast card actually wrote, read back from the audit trail.
 *
 * Every Forecast V3 write already records a ForecastEvent carrying the before
 * and after of the change (utils/forecastEventsStore.js, and the twelve
 * logForecastEvent sites behind the write routes). That trail is what the
 * Notes page timeline renders. This surfaces the same records in chat, so
 * "Applied 3 of 3 changes" can be expanded into WHICH three and from what to
 * what — read back from the server rather than echoed from the request, which
 * is the point: it shows what was committed, not what was asked for.
 *
 * Scoping lives in selectAppliedEvents.js — this version, the accounts the
 * card touched, since apply started. That window can in principle catch a
 * simultaneous manual edit to the same account in the same seconds; that is
 * rare, and the alternative — echoing the request back — would show changes
 * that failed as though they had landed.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function eventPeriod(event) {
  if (event.month) return `${MONTHS[event.month - 1]}${event.year ? ` ${event.year}` : ''}`;
  return event.year ? String(event.year) : '';
}

function eventTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function ForecastChangeHistory({ orgId, locationId, versionId, since, accountIds = [], year = null }) {
  const [state, setState] = useState({ status: 'loading', events: [], error: null });

  useEffect(() => {
    let cancelled = false;
    if (!orgId || !versionId) {
      setState({ status: 'unavailable', events: [], error: null });
      return undefined;
    }
    (async () => {
      try {
        const res = await getEvents({
          orgId: Number(orgId),
          locationId: locationId != null ? Number(locationId) : undefined,
          versionId: Number(versionId),
          ...(year != null ? { year: Number(year) } : {}),
          limit: 100
        });
        if (cancelled) return;
        const events = selectAppliedEvents({ events: res.events, since, accountIds });
        setState({ status: 'ready', events, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          events: [],
          error: err?.response?.data?.error || err?.message || 'Could not load the change history.'
        });
      }
    })();
    return () => { cancelled = true; };
    // accountIds is rebuilt each render by the parent; key on its content.
  }, [orgId, locationId, versionId, since, year, (accountIds || []).join(',')]);

  if (state.status === 'loading') {
    return <div className="fch-empty">Loading the change history…</div>;
  }
  if (state.status === 'unavailable') {
    return <div className="fch-empty">The change history needs an organization and forecast version in scope.</div>;
  }
  if (state.status === 'error') {
    return <div className="fch-empty fch-error">{state.error}</div>;
  }
  if (state.events.length === 0) {
    // Not an error: some change kinds (a driver build committed in bulk) write
    // through paths that record against the department rather than an account.
    return <div className="fch-empty">No individual change records came back for this version.</div>;
  }

  return (
    <ul className="fch-list">
      {state.events.map(event => (
        <li key={event.id} className="fch-item">
          <div className="fch-item-main">
            <span className="fch-desc">{formatEventDescription(event)}</span>
            {eventPeriod(event) ? <span className="fch-period">{eventPeriod(event)}</span> : null}
          </div>
          <div className="fch-item-meta">
            {event.departmentName ? <span>{event.departmentName}</span> : null}
            <span>{event.createdByName || event.createdBy}</span>
            <span className="fch-time">{eventTime(event.createdAt)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default ForecastChangeHistory;
